import { LowerCasePipe, NgClass } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { RiderColor } from '@polaris-customerweb/sharedcomponents.core';
import {
    Adventure,
    Dealer,
    Place,
    RideArea,
} from '@polaris-customerweb/sharedcomponents.map';
import { ImageCdnSrcDirective } from '@polaris-customerweb/sharedcomponents.shared';
import {
    PlannedRide,
    PlannedRideViapoint,
    PlannedRideWaypoint,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { filter, map, take, takeWhile, withLatestFrom } from 'rxjs';
import {
    selectActivePlannedRide,
    selectEditablePlannedRide,
} from 'src/app/components/planned-ride/planned-ride.reducer';
import { AnalyticsDataDirective } from 'src/app/directives/analytics-data.directive';
import { AnalyticsDirective } from 'src/app/directives/analytics.directive';
import { AppState } from 'src/app/models/app-state';
import { MapPoint } from 'src/app/models/map-point';
import { PlannedRideService } from 'src/app/services/planned-ride.service';
import { PlannedRideWarningMessageComponent } from '../planned-ride-warning-message/planned-ride-warning-message.component';

@Component({
    imports: [
        AnalyticsDataDirective,
        AnalyticsDirective,
        AngularSvgIconModule,
        ImageCdnSrcDirective,
        LowerCasePipe,
        NgClass,
        PlannedRideWarningMessageComponent,
    ],
    selector: 'rdx-planned-ride-stop',
    standalone: true,
    styleUrls: ['./planned-ride-stop.component.scss'],
    templateUrl: './planned-ride-stop.component.html',
})
export class PlannedRideStopComponent implements OnInit, OnChanges, OnDestroy {
    @Input() mapPoint: MapPoint;
    @Input() popupMode = true;
    @Output() addMapPointToPlannedRide = new EventEmitter<void>();
    @Output() removeMapPointFromPlannedRide = new EventEmitter<{
        mapPoint: MapPoint;
        deleteSegments: boolean;
    }>();
    pointIsRoutable = false;
    canAdd = true;
    disableButton = false;
    isAddAgain = false;
    nextStopNumber?: number;
    isViapoint = false;
    canDelete = false;
    isShowingViapointDeleteOptions = false;
    isShowingWaypointDeleteOptions = false;
    canDeleteSegments = false;
    points: PlannedRideWaypoint[] = [];
    color: RiderColor;
    entityType: string;
    maxPoints: number;
    ride?: PlannedRide;

    private isDestroyed = false;

    constructor(
        private configService: ConfigService,
        private plannedRideService: PlannedRideService,
        private store: Store<AppState>
    ) {
        this.maxPoints = plannedRideService.MAX_POINTS;
    }

    ngOnInit(): void {
        this.store
            .select(selectEditablePlannedRide)
            .pipe(
                filter((x) => Boolean(x)),
                withLatestFrom(this.store.select(selectActivePlannedRide)),
                map(([editRide, ride]) =>
                    this.configService.isOffRoad ? ride : editRide
                ),
                // popups don't trigger the ngOnDestroy method
                this.popupMode ? take(1) : takeWhile(() => !this.isDestroyed)
            )
            .subscribe((ride) => {
                this.ride = ride;
                if (this.mapPoint && this.ride) {
                    this.setValues();
                }
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.mapPoint && this.ride) {
            this.setValues();
        }
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
    }

    setValues() {
        this.isShowingViapointDeleteOptions = false;
        this.isShowingWaypointDeleteOptions = false;
        this.pointIsRoutable = this.getPointIsRoutable();
        if (this.pointIsRoutable) {
            this.isViapoint = this.mapPoint instanceof PlannedRideViapoint;
            if (!this.isViapoint) {
                this.points =
                    this.ride?.waypoints.filter(
                        (x) =>
                            x.id === this.mapPoint.id ||
                            (Boolean(this.mapPoint.id) &&
                                x.originalId === this.mapPoint.id) ||
                            (this.mapPoint instanceof PlannedRideWaypoint &&
                                this.mapPoint.originalId &&
                                x.originalId === this.mapPoint.originalId) ||
                            (x.geoCoordinate.latitude ===
                                this.mapPoint.geoCoordinate.latitude &&
                                x.geoCoordinate.longitude ===
                                    this.mapPoint.geoCoordinate.longitude)
                    ) || [];
                this.mapPoint = this.points[0] ?? this.mapPoint;
                this.canAdd = !this.isLastPoint();
                this.isAddAgain = this.points.length > 0;
            }

            this.disableButton =
                (this.ride?.points?.length ?? 0) >= this.maxPoints;

            this.canDelete =
                (this.mapPoint instanceof PlannedRideWaypoint &&
                    this.points.length > 0) ||
                this.mapPoint instanceof PlannedRideViapoint;
            this.nextStopNumber =
                this.mapPoint instanceof PlannedRideViapoint
                    ? this.ride?.getNextStopNumber(this.mapPoint)
                    : this.ride?.nextWaypoint;
            this.canDeleteSegments = Boolean(
                this.mapPoint instanceof PlannedRideViapoint &&
                    this.ride?.hasSurroundingViapoint(this.mapPoint)
            );
            this.color = this.ride?.color ?? 'Blue';
            this.entityType =
                this.plannedRideService.getRoutableItemAnalyticsEntityType(
                    this.mapPoint
                ) || '';
        }
    }

    delete() {
        if (this.canDeleteSegments) {
            this.isShowingViapointDeleteOptions =
                !this.isShowingViapointDeleteOptions;
        } else if (this.points.length > 1) {
            this.isShowingWaypointDeleteOptions =
                !this.isShowingWaypointDeleteOptions;
        } else {
            this.emitDelete();
        }
    }

    emitDelete(point?: PlannedRideWaypoint, deleteSegments = false) {
        this.removeMapPointFromPlannedRide.emit({
            deleteSegments,
            mapPoint: point ?? this.mapPoint,
        });
    }

    private getPointIsRoutable() {
        return (
            this.mapPoint instanceof Adventure ||
            this.mapPoint instanceof Dealer ||
            this.mapPoint instanceof Place ||
            this.mapPoint instanceof PlannedRideWaypoint ||
            this.mapPoint instanceof PlannedRideViapoint ||
            this.mapPoint instanceof RideArea ||
            this.mapPoint instanceof Waypoint
        );
    }

    private isLastPoint(): boolean {
        if (
            this.mapPoint instanceof PlannedRideWaypoint &&
            this.points.length > 0
        ) {
            const currentCoordinates =
                this.points[this.points.length - 1].geoCoordinate;
            const lastRideCoordinates =
                this.ride?.points[this.ride.points.length - 1].geoCoordinate;

            return (
                currentCoordinates.latitude === lastRideCoordinates?.latitude &&
                currentCoordinates.longitude === lastRideCoordinates.longitude
            );
        }
        return false;
    }
}
